import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons/faMagnifyingGlass';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';

import FaIcon from 'ravenjs/lib/FaIcon';
import { renderCheck } from 'ravenjs/utils/viewport';
import { get } from 'ravenjs/utils/lodash';

import AdvancedSearchMenu from './AdvancedSearchMenu';
import SearchInput from './SearchInput';
import SearchInputButton from './SearchInputButton';
import SearchInputContainer from './SearchBoxContainer';

function SearchBox(props) {
    const {
        advancedSearch,
        advancedSearchSelect,
        disabled,
        onCancel,
        onChange,
        onClick,
        value,
        onChangeAdvancedSearch,
        onAdvancedSearchCancel,
        searchStyleProps,
        inputRef,
        ...inputProps
    } = props;
    // Create a ref to the `SearchInput`.
    const inputEl = React.useRef(inputRef || null);
    // Create a local state for the `SearchInput`.
    const [searchDisabled, setSearchDisabled] = React.useState(disabled);
    const [searchSpec, setSearchSpec] = React.useState(value || '');
    const [inputFocused, setFocusOnInput] = React.useState(false);
    const [advancedSearchSpec, setAdvancedSearch] = React.useState(advancedSearch || []);
    const searchBoxBorderColor = get(searchStyleProps, 'searchBoxBorderColor', '');
    const searchBoxMinWidth = get(searchStyleProps, 'minWidth', '');

    useEffect(() => {
        if (inputRef && inputRef !== inputEl.current) {
            inputEl.current = inputRef;
        }
    }, [inputRef]);

    // Are we rendering the advanced search?
    const showAdvancedSearch =
        Array.isArray(advancedSearchSpec) && advancedSearchSpec.length > 0 && advancedSearchSelect;

    // Set the search icon.
    const searchIcon = !searchSpec ? faMagnifyingGlass : faXmark;
    // Handle the `onClick` for the `SearchInputButton`.
    function handleBtnOnClick(event) {
        event.preventDefault();
        // If no `searchSpec` present, focus on
        if (!searchSpec) {
            inputEl.current.focus();
            return;
        }

        const clearMenuInputs = advancedSearchSpec.map(menu => {
            return {
                ...menu,
                value: '',
            };
        });
        // Clear the `searchSpec`.
        setSearchSpec('');
        setAdvancedSearch(clearMenuInputs);
        // Invoke the `onCancel`.
        typeof onCancel === 'function' && onCancel(event);
    }
    // Handle the `onChange` for the `SearchInput`.
    function handleInputOnChange(event) {
        setSearchSpec(event.target.value);
        // Invoke the `onChange`.
        typeof onChange === 'function' && onChange(event.target.value, event);
    }

    // Handle the `onChange` for the `AdvanceSearchInput`.
    function handleAdvanceSearchInputOnChange(key) {
        setSearchSpec(key);
    }

    function handleAdvanceSearchOnClear(event, advanceSearchMenu, advanceSearchString) {
        setAdvancedSearch(advanceSearchMenu);
        setSearchSpec(advanceSearchString);
        setSearchDisabled(true);
        typeof onAdvancedSearchCancel === 'function' && onAdvancedSearchCancel(advanceSearchString);
    }

    // Handle the `onChange` for the `AdvanceSearchInput`.
    function handleAdvanceSearchOnClick(menuItems, advanceSearchData) {
        setSearchSpec(advanceSearchData);
        // Invoke the `onChange`.
        typeof onChange === 'function' && onChangeAdvancedSearch(advanceSearchData, menuItems);
    }

    // Handle the `onBlur` for the `SearchInput`.
    function handleInputOnBlur(event) {
        setFocusOnInput(false);
    }
    // Handle the `onFocus` for the `SearchInput`.
    function handleInputOnFocus(event) {
        setFocusOnInput(true);
    }
    // Handle the `onOpen` event for the `AdvancedSearchMenu`.
    function handleAdvancedSearchOnOpen() {
        setSearchDisabled(true);
    }
    // Handle the `onClose` event for the `AdvancedSearchMenu`.
    function handleAdvancedSearchOnClose() {
        setSearchDisabled(false);
    }
    // Render the `SearchBox`.
    return (
        <SearchInputContainer
            disabled={searchDisabled}
            focused={inputFocused}
            readOnly={searchDisabled}
            maxWidth={renderCheck('md', 'less') ? 'unset' : '275px'}
            minWidth={renderCheck('md', 'less') ? 'unset' : searchBoxMinWidth}
            borderColor={searchBoxBorderColor}
        >
            <SearchInputButton
                data-testid="search-input-button"
                onClick={handleBtnOnClick}
                disabled={searchDisabled}
            >
                <FaIcon icon={searchIcon} height="15px" width="15px" />
            </SearchInputButton>
            <SearchInput
                {...inputProps}
                type="search"
                enterKeyHint="go"
                disabled={searchDisabled}
                onBlur={handleInputOnBlur}
                onChange={handleInputOnChange}
                onFocus={handleInputOnFocus}
                readOnly={searchDisabled}
                ref={inputRef || inputEl}
                showAdvancedSearch={showAdvancedSearch}
                value={searchSpec}
            />
            {showAdvancedSearch && (
                <AdvancedSearchMenu
                    menuItems={advancedSearchSpec}
                    onClose={handleAdvancedSearchOnClose}
                    onOpen={handleAdvancedSearchOnOpen}
                    onChange={handleAdvanceSearchInputOnChange}
                    onClick={handleAdvanceSearchOnClick}
                    onClear={handleAdvanceSearchOnClear}
                    searchSpec={searchSpec}
                />
            )}
        </SearchInputContainer>
    );
}

SearchBox.propTypes = {
    advancedSearch: PropTypes.array,
    disabled: PropTypes.bool,
    advancedSearchSelect: PropTypes.bool,
    onCancel: PropTypes.func,
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    value: PropTypes.string,
    onChangeAdvancedSearch: PropTypes.func,
    onAdvancedSearchCancel: PropTypes.func,
    searchStyleProps: PropTypes.object,
    inputRef: PropTypes.object,
};

SearchBox.defaultProps = {
    advancedSearch: null,
    advancedSearchSelect: null,
    disabled: null,
    onCancel: null,
    onChange: null,
    onClick: null,
    value: '',
    onChangeAdvancedSearch: null,
    onAdvancedSearchCancel: null,
    searchStyleProps: {},
    inputRef: null,
};

export default React.memo(SearchBox);
